import { http } from 'App/http';
import axios from 'axios';
import Vue from 'vue';
import mixpanel from "mixpanel-browser";
import store from '../store'

const Observer = (new Vue()).$data.__ob__.constructor;

export function makeNonreactive(obj) {
    obj.__ob__ = new Observer({});
    return obj;
}

function _buildFormData(formData, data, parentKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
        Object.keys(data).forEach(key => {
            _buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
        });
    } else if (parentKey[0] !== '_' && typeof value !== 'function') {
        const value = data == null ? '' : data;
  
        formData.append(parentKey, value);
    }
}

export function objectToFormData(data) {
    const formData = new FormData();
  
    _buildFormData(formData, data);
  
    return formData;
}

export function getParameterByName(name, url = null) {
    if (!url) {
        url = window.location.href;
    }

    name = name.replace(/[\[\]]/g, '\\$&');

    var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
        results = regex.exec(url);

    if (!results) {
        return null;
    }

    if (!results[2]) {
        return '';
    }

    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

class FileUploaderService {
    async store(file, options = {}) {
        const response = await http.post('/app/signed-storage-url', {
            file_name: file.name,
            content_type: options.contentType || file.type,
            expires: options.expires || '',
        }, {
            baseURL: options.baseURL || null,
            headers: options.headers || {}
        });

        let headers = response.data.headers;
        const uploadUrl = response.data.url;
        const cancelToken = options.cancelToken || '';

        if ('Host' in headers) {
            delete headers.Host;
        }

        if (typeof options.progress === 'undefined') {
            options.progress = () => {};
        }

        // The application HTTP client includes CSRF and X-Requested-With headers.
        // Those headers are not part of the S3 signature or bucket CORS contract.
        const uploadOrigin = new URL(uploadUrl, window.location.href).origin;
        const uploadClient = uploadOrigin === window.location.origin ? http : axios;
        await uploadClient.put(uploadUrl, file, {
            cancelToken: cancelToken,
            headers: headers,
            onUploadProgress: (progressEvent) => {
                options.progress(progressEvent.loaded / progressEvent.total);
            }
        })

        response.data.extension = file.name.split('.').pop()

        return response.data;
    }
}

export const FileUploader = new FileUploaderService();

export function randomId() {
    return Math.random().toString(36).substring(2);
}

export function getByteLength(normal_val) {
    // Force string type
    normal_val = String(normal_val);
    var byteLen = 0;

    for (var i = 0; i < normal_val.length; i++) {
        var c = normal_val.charCodeAt(i);
        byteLen +=  c < (1 <<  7) ? 1 :
                    c < (1 << 11) ? 2 :
                    c < (1 << 16) ? 3 :
                    c < (1 << 21) ? 4 :
                    c < (1 << 26) ? 5 :
                    c < (1 << 31) ? 6 : Number.NaN;
    }

    return parseInt(byteLen);
}

export function getQueryParam(name = null) {
    let params = {};
    let queryStringSplit = window.location.search.replace(/^\?/, '').split('&');

    queryStringSplit.forEach(queryParam => {
        let [key, value] = queryParam.split('=');
        params[key] = decodeURIComponent(value);
    });

    if (name) {
        return params[name];
    }

    return params;
}

export function insertQueryParam(key, value) {
    if ('URLSearchParams' in window) {
        var searchParams = new URLSearchParams(window.location.search)
        searchParams.set(key, value);
        var newRelativePathQuery = window.location.pathname + '?' + searchParams.toString();
        history.pushState(null, '', newRelativePathQuery);
    }
}

export function minutesToHumanFormat(minutes) {
    let hours = Math.floor(minutes / 60);
    minutes = minutes % 60;

    if (hours == 1 && minutes == 0) {
        return `1 ${trans('online_booking.hour')}`;
    }

    if (hours == 1 && minutes > 0) {
        return `1 ${trans('online_booking.hour')} ${trans('global.and')} ${minutes} ${trans('online_booking.minutes')}`;
    }

    if (hours > 1 && minutes == 0) {
        return `${hours} ${trans('online_booking.hours')}`;
    }

    if (hours > 1 && minutes > 0) {
        return `${hours} ${trans('online_booking.hours')} ${trans('global.and')} ${minutes} ${trans('online_booking.minutes')}`;
    }

    if (minutes > 1) {
        return `${minutes} ${trans('online_booking.minutes')}`;
    }

    return `1 ${trans('products.minute')}`;
}

const GSM_ALPHABET_REGEX = "[^A-Za-z0-9 \\r\\n@£$¥èéùìòÇØøÅå\u0394_\u03A6\u0393\u039B\u03A9\u03A0\u03A8\u03A3\u0398\u039EÆæßÉ!\"#$%&'()*+,\\-./:;<=>?¡ÄÖÑÜ§¿äöñüà^{}\\\\\\[~\\]|\u20AC]";

export function hasNonGSMCharacters(text) {
    var regexp = new RegExp(GSM_ALPHABET_REGEX, 'gi');

    return regexp.test(text);
}

export function removeNonGSMCharacters(text) {
    var regexp = new RegExp(GSM_ALPHABET_REGEX, 'gi');

    return String(text).replace(regexp, '?');
}

export function copyToClipboard(text, toastrMessage = null) {
    if (!text) {
        return;
    }

    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    if (toastrMessage) {
        toastr.success(toastrMessage);
    }
}

export function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&'); // $& means the whole matched string
}

class PhoneNumber
{
    constructor(rawNumber, user) {
        this.rawNumber = rawNumber;
        this.user = user;
        this.parse();
    }

    parse(rawNumber = null, user = null) {
        rawNumber = rawNumber || this.rawNumber;
        user = user || this.user;

        if (!user || !user.country) {
            throw new Error('Please provide a user with a "country" attribute.');
        }

        this.rawNumber = rawNumber;
        this.user = user;

        rawNumber = String(rawNumber).replace(/[^\\+0-9]/gi, ' ');
        const defaultCountryCode = Config.get(`countries.${user.country}.phone_contry_code`);
        let countryCode = null;
        let number = null;

        if (rawNumber.substring(0, 2) === '00') {
            // in many countries, two leading zeroes mean it's an international phone number
            rawNumber = rawNumber.replace(/^0+/gi, '');
        }

        if (rawNumber[0] === '0') {
            this.number = rawNumber.replace(/^0+/gi, '');
            this.countryCode = this.number !== '' ? String(defaultCountryCode) : '';
            return;
        }

        if (rawNumber[0] === '+' && rawNumber.indexOf(' ') === -1) {
            throw new Error('Cannot parse a phone number without a space between country code and number.');
        }

        if (rawNumber[0] === '+' && rawNumber.indexOf(' ') > 0) {
            rawNumber = rawNumber.replace(/[^0-9 ]/gi, '');

            [countryCode, number] = rawNumber.split(' ', 2);
        }

        this.number = number || rawNumber.replace(/[^0-9]/gi, '');
        this.countryCode = this.number !== '' ? String(countryCode || '') : '';
    }

    price() {
        if (this.isInternational()) {
            return Config.get(`countries.${this.user.country}.sms_message_price_international`);
        }
    
        return Config.get(`countries.${this.user.country}.sms_message_price`);
    }

    priceFormatted() {
        return Config.get(`countries.${this.user.country}.subscription_currency`) + " " + format_amount(this.price() / 100, 2)
    }

    isInternational() {
        let defaultCountryCode = Config.get(`countries.${this.user.country}.phone_contry_code`);
        return this.countryCode !== '' && this.countryCode !== String(defaultCountryCode);
    }
}

// A simpler version of the PHP's PhoneNumber
export function phoneNumber(rawNumber, user) {
    return new PhoneNumber(rawNumber, user);
}


// Aliasing a mixpanel user from our website
export function mixpanelAlias(user_id) {
    if(Config.get('app.PRACTIZE_ANALYTICS_ENABLED') && Config.get('app.MIXPANEL_TOKEN')){
        mixpanel.init(Config.get('app.MIXPANEL_TOKEN'));
        mixpanel.alias(user_id);
    }
}

// Tracking a mixpanel event
export function mixpanelTrackEvent(event, properties) {

    if(Config.get('app.PRACTIZE_ANALYTICS_ENABLED') && Config.get('app.MIXPANEL_TOKEN')){

        let props = {};
        if (properties !== undefined) {
            props = properties;
        }

        mixpanel.init(Config.get('app.MIXPANEL_TOKEN'));
        mixpanel.identify(App.user_id);
        mixpanel.track(event, props);

    }
}

export function handleRemainingSessionTimeChange(value) {
    if (value === store.getters['auth/sessionExpiringWarningTimeout']) {
        store.dispatch('auth/fetchSessionDetails')
            .then((showSessionExpiring) => {
                if (showSessionExpiring) {
                    setTimeout(() => {
                        swal({
                            title: trans('login.session_expiring_title'),
                            text: trans('login.session_expiring_description'),
                            type: 'warning',
                            confirmButtonText: trans('login.session_extend'),
                        }, (confirmed) => {
                            if (confirmed) {
                                store.dispatch('auth/fetchSessionDetails', { force: true })
                            }
                        });
                    }, 500)
                }
            })
    } else if (value < 0) {
        store.dispatch('auth/fetchSessionDetails')
            .then((showSessionExpiring) => {
                if (showSessionExpiring) {
                    store.commit('auth/setCurrentTimeTimeout', null)
                    window.location.href = '/login'
                } else {
                    swal.close()
                }
            })
    }
}

export const replaceAll = (str, find, replace) => {
    return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
}
